import { BrandNavigationData, DiscountState, MOQState } from "@/types";
import { ManufacturersResponse } from "@/types/home";
import { CartState } from "@/types/cart";
import { getCookie } from "cookies-next";
import {
  calculateDealerPrice,
  formatWithCommasWithoutDecimal,
} from "./helpers";

export const getBulkUserID = (): string | null => {
  const userid = getCookie("bulkUserID");
  return typeof userid === "string" ? userid : null;
};

export const getCreditLimit = (): string | null => {
  const creditLimit = getCookie("bulkCreditLimit");
  return typeof creditLimit === "string" ? creditLimit : null;
};

export const getBrandNavigationData = (
  cart: CartState | null,
  brand: string | null,
  manufacturers: ManufacturersResponse | null
): BrandNavigationData => {
  const allManufacturers = [
    ...(manufacturers?.domestic || []),
    ...(manufacturers?.premium || []),
    ...(manufacturers?.others || []),
  ];
  const manufacturer = allManufacturers?.find(
    (manu) => manu.manufacture_name === brand
  );
  if (!manufacturer) {
    return {
      totalBrandCartValue: 0,
      MOQPrice: 0,
      discountPrice: 0,
      discountPercentage: 0,
      BeforeMOQ: 0,
      MOQProgress: 0,
      DiscountProgress: 0,
      MOQState: {
        type: "MOV",
        state: "processing",
        level1: "Minimum Order Value",
        level2: "",
      },
      DiscountState: {
        type: "Discount",
        state: "processing",
        level1: "",
        level2: "",
      },
    };
  }

  // Find manufacturer details for the given brand
  const cartManufacturer = cart?.manufacturers.find(
    (manu) => manu.manufacture_name === brand
  );

  if (!manufacturer) {
    return {
      totalBrandCartValue: 0,
      MOQPrice: 0,
      discountPrice: 0,
      discountPercentage: 0,
      BeforeMOQ: 0,
      MOQProgress: 0,
      DiscountProgress: 0,
      MOQState: {
        type: "MOV",
        state: "processing",
        level1: "Minimum Order Value",
        level2: "",
      },
      DiscountState: {
        type: "Discount",
        state: "processing",
        level1: "",
        level2: "",
      },
    };
  }

  const MOQPrice = manufacturer?.MOQ_slab || 0; // Minimum Order Quantity price
  const discountPrice = manufacturer?.additional_discount_slab || 0; // Price for extra discount
  const discountRate = parseFloat(manufacturer?.discount || "0"); // Discount percentage

  // Calculate total brand cart value
  const totalBrandCartValue =
    cartManufacturer?.products?.reduce((total, product) => {
      const quantity = Number(product?.buy_quantity) || 0;
      const saleQuantity = product?.Sales_Qunatity;
      const salePrice = product?.Sales_Price;
      return (
        total +
        calculateDealerPrice(
          parseInt(product?.MRP_price),
          parseInt(
            quantity && saleQuantity !== null && quantity >= saleQuantity
              ? salePrice
              : product?.NDP_Price
          ) || 0,
          product?.GST
        ) *
          product?.buy_quantity
      );
    }, 0) || 0;

  const totalBrandCartValueWithoutGST =
    cartManufacturer?.products?.reduce((total, product) => {
      const quantity = Number(product?.buy_quantity) || 0;
      const saleQuantity = Number(product?.Sales_Qunatity) || 0;
      const salePrice = product?.Sales_Price;
      return (
        total +
          parseInt(
            quantity && saleQuantity !== null && quantity >= saleQuantity
              ? salePrice
              : product?.NDP_Price
          ) * product?.buy_quantity
      );
    }, 0) || 0;

  // Apply discount only if total value meets the additional discount slab
  const discountPercentage =
    totalBrandCartValue >= discountPrice ? discountRate : 0;

  // Determine the progress for MOQ and discount
  const BeforeMOQ = Math.min(totalBrandCartValue / MOQPrice, 1) * 3;

  // MOQProgress should be based on (discountPrice - MOQPrice), only when totalBrandCartValue > MOQPrice
  const MOQProgress =
    totalBrandCartValue > MOQPrice
      ? Math.min(
          (totalBrandCartValue - MOQPrice) / (discountPrice - MOQPrice),
          1
        ) * 3
      : 0;

  // DiscountProgress should be based on (500000 - discountPrice), only when totalBrandCartValue > discountPrice
  const DiscountProgress =
    totalBrandCartValue > discountPrice
      ? Math.min(
          (totalBrandCartValue - discountPrice) / (1000000 - discountPrice),
          1
        ) * 3
      : 0;

  // Define MOQ state
  const MOQState: MOQState = {
    type: "MOV",
    state:
      totalBrandCartValue > 0 && totalBrandCartValue >= MOQPrice
        ? "done"
        : "processing",
    level1:
      totalBrandCartValue > 0 && totalBrandCartValue >= MOQPrice
        ? "MOV Met for this order"
        : "Minimum Order Value",
    level2:
      totalBrandCartValue > 0 && totalBrandCartValue >= MOQPrice
        ? ""
        : `Add items worth ₹${(MOQPrice - totalBrandCartValue).toFixed(
            2
          )} to meet MOV`,
  };

  // Define Discount state
  const DiscountState: DiscountState = {
    type: "Discount",
    state: totalBrandCartValue >= discountPrice ? "done" : "processing",
    level1:
      totalBrandCartValue >= discountPrice
        ? `Congratulations, you saved ₹${formatWithCommasWithoutDecimal(
            Math.round((discountRate / 100) * totalBrandCartValueWithoutGST)
          )} in your cart value`
        : "Unlock Amazing Discounts",
    level2:
      totalBrandCartValue >= discountPrice
        ? ""
        : `Add items worth ₹${(discountPrice - totalBrandCartValue).toFixed(
            2
          )} to Save ${discountRate}% on your order`,
  };

  return {
    totalBrandCartValue,
    MOQPrice,
    discountPrice,
    discountPercentage,
    BeforeMOQ,
    MOQProgress,
    DiscountProgress,
    MOQState,
    DiscountState,
  };
};
